import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import { useAuth } from '@/features/auth/useAuth'
import { registerSchema, type RegisterForm } from '@/features/auth/schemas'
import { ApiError } from '@/shared/api/client'
import { AuthShell } from '@/pages/AuthShell'
import { Button } from '@/shared/ui/Button'
import { Callout } from '@/shared/ui/Callout'
import { Field } from '@/shared/ui/Field'
import { cn } from '@/shared/lib/cn'

const ROLES = [
  { value: 'student', label: 'Ученик' },
  { value: 'teacher', label: 'Учитель' },
] as const

export function RegisterPage() {
  const { register: registerUser } = useAuth()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const [formError, setFormError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<RegisterForm>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      role: params.get('роль') === 'учитель' ? 'teacher' : 'student',
      last_name: '',
      grade: '',
    },
  })

  const role = watch('role')

  const onSubmit = async (values: RegisterForm) => {
    setFormError(null)
    try {
      await registerUser({
        email: values.email,
        password: values.password,
        first_name: values.first_name,
        last_name: values.last_name,
        role: values.role,
        grade: values.role === 'student' && values.grade !== '' ? Number(values.grade) : null,
      })
      navigate('/кабинет', { replace: true })
    } catch (error) {
      if (error instanceof ApiError) {
        // Ошибки отдельных полей показываем у этих полей, а не общим сообщением.
        const fieldErrors = error.fieldErrors
        if (fieldErrors.length > 0) {
          for (const item of fieldErrors) {
            if (item.field in values) {
              setError(item.field as keyof RegisterForm, { message: item.message })
            }
          }
          return
        }
        if (error.code === 'email_taken') {
          setError('email', { message: 'Этот email уже занят' })
          return
        }
        setFormError(error.message)
        return
      }
      // Сюда попадают только ошибки вне ApiError — показываем, что известно.
      setFormError(
        error instanceof Error
          ? `Не удалось зарегистрироваться: ${error.message}`
          : 'Не удалось зарегистрироваться. Попробуйте позже.',
      )
    }
  }

  return (
    <AuthShell
      title="Регистрация"
      footer={
        <>
          Уже есть аккаунт?{' '}
          <Link to="/вход" className="text-accent underline-offset-4 hover:underline">
            Войти
          </Link>
        </>
      }
    >
      <form noValidate onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
        {formError && <Callout tone="error">{formError}</Callout>}

        <fieldset>
          <legend className="text-meta font-medium text-ink-soft">Кто вы</legend>
          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            {ROLES.map((item) => (
              <button
                key={item.value}
                type="button"
                aria-pressed={role === item.value}
                onClick={() => setValue('role', item.value)}
                className={cn(
                  'min-h-11 rounded-md border px-3 text-left transition-colors duration-DEFAULT',
                  role === item.value
                    ? 'border-accent bg-raised'
                    : 'border-line bg-surface hover:border-ink-muted',
                )}
              >
                <span className="block text-meta font-medium text-ink">{item.label}</span>
              </button>
            ))}
          </div>
        </fieldset>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Имя" autoComplete="given-name" error={errors.first_name?.message} {...register('first_name')} />
          <Field label="Фамилия" autoComplete="family-name" error={errors.last_name?.message} {...register('last_name')} />
        </div>

        {role === 'student' && (
          <Field
            label="Класс"
            type="number"
            min={1}
            max={11}
            error={errors.grade?.message}
            {...register('grade')}
          />
        )}

        <Field label="Email" type="email" autoComplete="email" error={errors.email?.message} {...register('email')} />
        <Field
          label="Пароль"
          type="password"
          autoComplete="new-password"
          hint="Не меньше 8 символов, буквы и цифры"
          error={errors.password?.message}
          {...register('password')}
        />

        <Button type="submit" loading={isSubmitting} className="mt-1 w-full">
          Создать аккаунт
        </Button>
      </form>
    </AuthShell>
  )
}
